<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Contas Financeiras</h5>
      <b-button
        v-if="hasPermission('adicionar_contas_financeiras')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Novo Cadastro"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.apelido"
        placeholder="Filtrar por Apelido"
        class="form-control"
        @input="filtrar"
      />
      <input
        v-model="filters.instituicao"
        placeholder="Filtrar por Instituição"
        class="form-control"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        class="d-flex align-items-center"
        @click="limparFiltros"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('apelido')">
              Apelido {{ arrow('apelido') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('instituicao')">
              Instituição {{ arrow('instituicao') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('agencia')">
              Agência {{ arrow('agencia') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('numero_conta')">
              Nº Conta {{ arrow('numero_conta') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('responsavel_nome')">
              Responsável {{ arrow('responsavel_nome') }}
            </button>
          </th>
          <th class="text-end">Saldo Atual</th>
          <th class="text-center">Padrão</th>
          <th>Boletos</th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="c in contas" :key="c.id">
          <td>{{ c.apelido }}</td>
          <td>{{ c.instituicao }}</td>
          <td>{{ c.agencia }}</td>
          <td>{{ c.numero_conta }}</td>
          <td>{{ c.responsavel_nome }}</td>
          <td class="text-end">
            <span v-if="saldosLoading[c.id]" class="text-muted">…</span>
            <span v-else>{{ formatCurrency(saldos[c.id]?.saldo_atual) }}</span>
          </td>
          <td class="text-center">
            <i
              v-if="c.conta_padrao"
              class="bi bi-check-circle-fill text-success"
              title="Conta padrão"
            ></i>
            <span v-else class="text-muted">—</span>
          </td>

          <td><span class="badge" :class="c.boletos_habilitados ? 'bg-success' : 'bg-secondary'">{{ c.boletos_habilitados ? 'Habilitado' : 'Desabilitado' }}</span></td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_contas_financeiras')"
              class="bi bi-eye-fill text-primary mx-1"
              title="Detalhar"
              @click="detalharConta(c.id)"
            ></i>
            <i
              v-if="hasPermission('detalhar_contas_financeiras')"
              class="bi bi-receipt-cutoff text-success mx-1"
              title="Extrato"
              @click="abrirExtrato(c.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_contas_financeiras')"
              class="bi bi-pencil-fill text-warning mx-1"
              title="Editar"
              @click="editarConta(c.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_contas_financeiras')"
              class="bi bi-trash-fill text-danger mx-1"
              title="Excluir"
              @click="excluirConta(c.id)"
            ></i>
          </td>

        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>

  <ModalExtratoConta
    v-if="showExtrato && extratoContaId"
    :conta-id="extratoContaId"
    @close="fecharExtrato"
  />

</template>

<script setup>
import { computed, watch, onMounted, getCurrentInstance,ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import axios from '@/services/axios'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'
import ModalExtratoConta from '@/components/Financeiro/ContasFinanceiras/ModalExtratoConta.vue'

const router = useRouter()
const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

const {
  items: contas,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  '/financeiro/contas-financeiras',
  { key: 'apelido', order: 'asc' }
)

const pageOptions = [10,15,20,25,50]
const startRow = computed(() => (page.value-1)*limit.value+1)
const endRow   = computed(() => Math.min(page.value*limit.value, totalItems.value))

const showExtrato = ref(false)
const extratoContaId = ref(null)

function abrirExtrato(id){
  extratoContaId.value = Number(id)
  showExtrato.value = true
}
function fecharExtrato(){
  showExtrato.value = false
  carregarSaldoConta(extratoContaId.value)
  extratoContaId.value = null
}

function formatCurrency(n){
  if (n == null) return 'Desconhecido';
  const v = typeof n === 'number' ? n : parseFloat(n || 0)
  return new Intl.NumberFormat('pt-BR', { style:'currency', currency:'BRL' })
    .format(isNaN(v) ? 0 : v)
}

// cache de saldos por conta na página
const saldos = reactive({})          // { [id]: { saldo_atual, ... } }
const saldosLoading = reactive({})   // { [id]: boolean }

async function carregarSaldoConta(id){
  if (!id) return
  try{
    saldosLoading[id] = true
    const { data } = await axios.get(`/financeiro/contas-financeiras/${id}/saldo`)
    saldos[id] = data
  } catch(e){
    saldos[id] = { saldo_atual: null }
  } finally{
    saldosLoading[id] = false
  }
}

// carrega saldos para as contas visíveis na página atual
async function carregarSaldosPagina(){
  const ids = (contas.value || []).map(c => c.id).filter(Boolean)
  await Promise.all(ids.map(id => {
    // Recarrega saldos ao atualizar a lista, sem reutilizar valores antigos.
    if (!saldosLoading[id]) return carregarSaldoConta(id)
  }))
}


watch(page,  (n,o) => n!==o && load())
watch(limit,(n,o) => { if(n!==o){ page.value=1; load() }})

// filtros
async function filtrar() {
  page.value = 1
  await load()
  await carregarSaldosPagina()
}
async function limparFiltros() {
  filters.value = { apelido:'', instituicao:'' }
  page.value = 1
  await load()
  await carregarSaldosPagina()
}
async function changeLimit() {
  page.value = 1
  await load()
  await carregarSaldosPagina()
}
async function changeSort(field) {
  sort(field)
  await load()
  await carregarSaldosPagina()
}

// navegação e CRUD
function irParaCadastro() {
  router.push('/financeiro/contas-financeiras/novo')
}
function detalharConta(id) {
  router.push(`/financeiro/contas-financeiras/${id}/detalhar`)
}
function editarConta(id) {
  router.push(`/financeiro/contas-financeiras/${id}`)
}
async function excluirConta(id) {
  const ok = await proxy.$confirm('Tem certeza que deseja excluir esta conta financeira?','Confirmação')
  if(!ok) return
  await axios.delete(`/financeiro/contas-financeiras/${id}`)
  proxy.$toast.success('Conta excluída!')
  load()
}

watch(page, async (n,o) => {
  if(n!==o){
    await load()
    await carregarSaldosPagina()
  }
})
watch(limit, async (n,o) => {
  if(n!==o){
    page.value = 1
    await load()
    await carregarSaldosPagina()
  }
})


onMounted(async () => {
  await fetchPermissions()
  filters.value = { apelido:'', instituicao:'' }
  await load()
  await carregarSaldosPagina()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
.bv-table-sort-icon { display: none !important; }
</style>
